import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import Topbar from "../components/layout/Topbar.jsx";
import TotpWidget from "../components/ui/TotpWidget.jsx";
import CompanySelector from "../components/ui/CompanySelector.jsx";
import CredentialModal from "../components/ui/CredentialModal.jsx";
import {
  getCredentials,
  getPassword,
  adminDeleteCredential,
  adminGetTotpCode,
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  getPolicy,
  savePolicy,
  adminLogCredentialAccess,
  adminCreateShare,
  adminGetShares,
  adminRevokeShare,
} from "../services/credentialService.js";
import { BRAND_AWARE_TYPES, resolveBrandDomain, faviconSrc } from "../utils/brandIcons.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const TYPES = [
  { id: "WEBSITE",     label: "Site Web",        icon: "bi-globe2",      color: "#3B5BDB" },
  { id: "APPLICATION", label: "Application",     icon: "bi-app",         color: "#6741D9" },
  { id: "DATABASE",    label: "Base de données", icon: "bi-database",    color: "#C2255C" },
  { id: "EMAIL",       label: "E-mail",          icon: "bi-envelope",    color: "#2F9E44" },
  { id: "SERVER",      label: "Serveur",         icon: "bi-server",      color: "#3D6B6B" },
  { id: "SSH",         label: "SSH",             icon: "bi-terminal",    color: "#1A1A1A" },
  { id: "VPN",         label: "VPN",             icon: "bi-shield-lock", color: "#0077B6" },
  { id: "WIFI",        label: "WiFi",            icon: "bi-wifi",        color: "#9B2AE8" },
  { id: "ROUTER",      label: "Routeur",         icon: "bi-router",      color: "#E85D04" },
  { id: "NAS",         label: "NAS",             icon: "bi-hdd-stack",       color: "#1864AB" },
  { id: "PRINTER",     label: "Imprimante",      icon: "bi-printer",         color: "#6B7280" },
  { id: "CLOUD_BACKUP",label: "Sauvegarde Cloud",icon: "bi-cloud-arrow-up",  color: "#15AABF" },
  { id: "IP_PHONE",    label: "Téléphonie IP",   icon: "bi-telephone-fill", color: "#0CA678" },
  { id: "PBX",         label: "PBX",             icon: "bi-diagram-3-fill", color: "#F08C00" },
  { id: "SWITCH",      label: "Switch",          icon: "bi-hdd-network-fill", color: "#495057" },
  { id: "OTHER",       label: "Autre",           icon: "bi-key",         color: "#F76707" },
];

// ─────────────────────────────────────────────
//  TYPES DE CHAMPS PERSONNALISÉS — façon PasswordBoss
//  Chaque champ additionnel a son propre type, qui détermine
//  la façon dont sa valeur est saisie/affichée (masquée pour
//  un mot de passe, sélecteur natif pour une date, etc.)
// ─────────────────────────────────────────────

// Côté admin, seul le scope COMPANY est disponible : les credentials
// PERSONAL appartiennent à un utilisateur (owner) et ne doivent jamais
// être visibles ni révélées ici, même en lecture — voir CredentialScope.java.
const SCOPES = [
  { id: "COMPANY", label: "Entreprise", icon: "bi-building" },
];

const FOLDER_COLORS = [
  "#3B5BDB", "#6741D9", "#C2255C", "#2F9E44",
  "#3D6B6B", "#0077B6", "#9B2AE8", "#E85D04",
];

// Résout les métadonnées d'un type
const typeMeta = (typeId) =>
  TYPES.find(t => t.id === (typeId || "OTHER").toUpperCase()) || TYPES[TYPES.length - 1];

// ─────────────────────────────────────────────
//  MINI VIGNETTES SVG PAR TYPE — identiques à MyPasswordsPage.jsx,
//  pour que l'affichage des credentials soit rigoureusement le même
//  partout dans l'application.
// ─────────────────────────────────────────────

const THUMB = {
  WEBSITE: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tw"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#4DABF7"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tw)"/>
      <circle cx="40" cy="24" r="14" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="1.6"/>
      <ellipse cx="40" cy="24" rx="6" ry="14" fill="none" stroke="rgba(255,255,255,.65)" strokeWidth="1.2"/>
      <line x1="26" y1="24" x2="54" y2="24" stroke="rgba(255,255,255,.65)" strokeWidth="1.2"/>
    </svg>
  ),
  APPLICATION: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="ta"><stop offset="0%" stopColor="#6741D9"/><stop offset="100%" stopColor="#9775FA"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#ta)"/>
      <circle cx="40" cy="24" r="9" fill="rgba(255,255,255,.85)"/>
      <line x1="40" y1="32" x2="40" y2="20" stroke="#6741D9" strokeWidth="2" strokeLinecap="round"/>
      <polyline points="35,24 40,19 45,24" stroke="#6741D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  DATABASE: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="td"><stop offset="0%" stopColor="#C2255C"/><stop offset="100%" stopColor="#F06595"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#td)"/>
      <ellipse cx="40" cy="14" rx="16" ry="5" fill="rgba(255,255,255,.85)"/>
      <rect x="24" y="14" width="32" height="9" fill="rgba(255,255,255,.72)"/>
      <ellipse cx="40" cy="23" rx="16" ry="5" fill="rgba(255,255,255,.85)"/>
      <rect x="24" y="23" width="32" height="9" fill="rgba(255,255,255,.6)"/>
      <ellipse cx="40" cy="32" rx="16" ry="5" fill="rgba(255,255,255,.85)"/>
    </svg>
  ),
  EMAIL: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="te"><stop offset="0%" stopColor="#2F9E44"/><stop offset="100%" stopColor="#69DB7C"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#te)"/>
      <rect x="16" y="13" width="48" height="30" rx="3" fill="rgba(255,255,255,.85)"/>
      <polyline points="16,13 40,30 64,13" fill="none" stroke="rgba(47,158,68,.8)" strokeWidth="1.8"/>
    </svg>
  ),
  SERVER: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="ts"><stop offset="0%" stopColor="#3D6B6B"/><stop offset="100%" stopColor="#63B3B3"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#ts)"/>
      <rect x="26" y="7" width="28" height="36" rx="3" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.55)" strokeWidth="1"/>
      {[11,19,27,35].map((y, i) => <g key={i}><rect x="28" y={y} width="24" height="5" rx="1.5" fill="rgba(255,255,255,.18)"/><circle cx="48" cy={y+2.5} r="1.8" fill={i<2?"#69DB7C":"#FFD43B"}/></g>)}
    </svg>
  ),
  SSH: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}>
      <rect width="80" height="48" fill="#1A1A1A"/>
      <circle cx="17" cy="13" r="2.5" fill="#FF5F57"/><circle cx="24" cy="13" r="2.5" fill="#FFBD2E"/><circle cx="31" cy="13" r="2.5" fill="#28C840"/>
      <text x="14" y="28" fontFamily="monospace" fontSize="5.5" fill="#58A6FF">$</text>
      <text x="20" y="28" fontFamily="monospace" fontSize="5.5" fill="#7EE787">ssh admin@host</text>
    </svg>
  ),
  VPN: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tvpn"><stop offset="0%" stopColor="#0077B6"/><stop offset="100%" stopColor="#00B4D8"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tvpn)"/>
      <rect x="26" y="14" width="28" height="20" rx="4" fill="rgba(255,255,255,.2)" stroke="rgba(255,255,255,.6)" strokeWidth="1.5"/>
      <path d="M34 24 L46 24 M40 18 L40 30" stroke="rgba(255,255,255,.9)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  WIFI: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tw2"><stop offset="0%" stopColor="#9B2AE8"/><stop offset="100%" stopColor="#DA77F2"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tw2)"/>
      <circle cx="40" cy="40" r="3.5" fill="white"/>
      <path d="M30,33 Q40,25 50,33" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="3" strokeLinecap="round"/>
      <path d="M22,26 Q40,13 58,26" fill="none" stroke="rgba(255,255,255,.65)" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  ROUTER: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tr"><stop offset="0%" stopColor="#E85D04"/><stop offset="100%" stopColor="#F48C06"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tr)"/>
      <rect x="20" y="22" width="40" height="10" rx="3" fill="rgba(255,255,255,.85)"/>
      {[28,34,40,46].map(x => <circle key={x} cx={x} cy="27" r="2" fill="#E85D04"/>)}
      <line x1="30" y1="22" x2="25" y2="14" stroke="rgba(255,255,255,.8)" strokeWidth="1.5"/>
      <line x1="50" y1="22" x2="55" y2="14" stroke="rgba(255,255,255,.8)" strokeWidth="1.5"/>
    </svg>
  ),
  NAS: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tnas"><stop offset="0%" stopColor="#1864AB"/><stop offset="100%" stopColor="#4DABF7"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tnas)"/>
      <rect x="24" y="10" width="32" height="28" rx="3" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.5)" strokeWidth="1"/>
      <rect x="28" y="15" width="24" height="4" rx="1.5" fill="rgba(255,255,255,.25)"/>
      <rect x="28" y="22" width="24" height="4" rx="1.5" fill="rgba(255,255,255,.25)"/>
      <rect x="28" y="29" width="24" height="4" rx="1.5" fill="rgba(255,255,255,.25)"/>
      <circle cx="48" cy="17" r="1.3" fill="rgba(255,255,255,.7)"/>
      <circle cx="48" cy="24" r="1.3" fill="rgba(255,255,255,.7)"/>
    </svg>
  ),
  PRINTER: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tpr"><stop offset="0%" stopColor="#6B7280"/><stop offset="100%" stopColor="#9CA3AF"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tpr)"/>
      <rect x="24" y="18" width="32" height="16" rx="3" fill="rgba(255,255,255,.2)" stroke="rgba(255,255,255,.5)" strokeWidth="1"/>
      <rect x="29" y="10" width="22" height="9" rx="1.5" fill="rgba(255,255,255,.3)"/>
      <rect x="29" y="30" width="22" height="7" rx="1.5" fill="rgba(255,255,255,.3)"/>
    </svg>
  ),
  CLOUD_BACKUP: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tcb"><stop offset="0%" stopColor="#15AABF"/><stop offset="100%" stopColor="#66D9E8"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tcb)"/>
      <path d="M28 30 a8 8 0 0 1 0-16 a11 11 0 0 1 21-3 a8 8 0 0 1 -2 19 z" fill="rgba(255,255,255,.9)"/>
      <path d="M40 20 v10 M35 26 l5 5 5-5" fill="none" stroke="#15AABF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  IP_PHONE: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tip"><stop offset="0%" stopColor="#0CA678"/><stop offset="100%" stopColor="#63E6BE"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tip)"/>
      <rect x="23" y="8" width="34" height="32" rx="4" fill="rgba(255,255,255,.9)"/>
      <rect x="27" y="13" width="26" height="8" rx="2" fill="#0CA678"/>
      {[0,1].map(r => (
        <g key={r}>
          {[0,1,2,3].map(c => <circle key={c} cx={30 + c*7} cy={27 + r*6} r="1.4" fill="#0CA678"/>)}
        </g>
      ))}
    </svg>
  ),
  PBX: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tpbx"><stop offset="0%" stopColor="#F08C00"/><stop offset="100%" stopColor="#FFC078"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tpbx)"/>
      <rect x="14" y="15" width="52" height="18" rx="3" fill="rgba(255,255,255,.88)"/>
      {[0,1,2,3,4].map(i => <rect key={i} x={19 + i*9} y="19" width="5" height="9" rx="1" fill="#F08C00"/>)}
    </svg>
  ),
  SWITCH: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tsw"><stop offset="0%" stopColor="#495057"/><stop offset="100%" stopColor="#868E96"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tsw)"/>
      <rect x="12" y="19" width="56" height="12" rx="2" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.55)" strokeWidth="1"/>
      {Array.from({length:8}).map((_,i) => <circle key={i} cx={17 + i*7} cy="25" r="1.3" fill={i%3===0 ? "#69DB7C":"rgba(255,255,255,.5)"}/>)}
    </svg>
  ),
  OTHER: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}>
      <rect width="80" height="48" fill="#f1f3f5"/>
      <circle cx="40" cy="24" r="12" fill="#F76707"/>
      <rect x="38" y="17" width="4" height="14" rx="2" fill="white"/>
      <rect x="33" y="22" width="14" height="4" rx="2" fill="white"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────
//  COMPOSANT — GÉNÉRATEUR DE MOT DE PASSE
//
//  Utilisé dans la modale de création/édition.
//  Appelle le backend pour générer un mot de passe conforme
//  à la politique de l'entreprise sélectionnée.
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
//  COMPOSANTS UTILITAIRES — COPIE RAPIDE
// ─────────────────────────────────────────────

/** Bouton copier un texte simple (username, custom field…) */
function CopyBtn({ value, title = "Copier" }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button className="cred-action-btn" onClick={handleCopy} title={title}
      style={{ color: copied ? "#10B981" : undefined }}>
      <i className={`bi ${copied ? "bi-check2" : "bi-copy"}`}/>
    </button>
  );
}

/** Bouton copier le mot de passe — appel API pour déchiffrer */
function CopyPasswordBtn({ credId, title = "Copier le mot de passe" }) {
  const [copied,  setCopied]  = useState(false);
  const [loading, setLoading] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const res = await getPassword(credId);
      await navigator.clipboard.writeText(res.data);
      adminLogCredentialAccess(credId, { targetType: "PASSWORD", action: "COPY" }).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button className="cred-action-btn" onClick={handleCopy} title={title}
      style={{ color: copied ? "#10B981" : undefined }}>
      <i className={`bi ${loading ? "bi-arrow-repeat spin" : copied ? "bi-check2" : "bi-key"}`}/>
    </button>
  );
}

/** Bouton copier le code TOTP actuel */
function CopyTotpBtn({ credId }) {
  const [copied,  setCopied]  = useState(false);
  const [loading, setLoading] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const res = await adminGetTotpCode(credId);
      const code = res?.data?.code || res?.code;
      if (!code) throw new Error("Code 2FA indisponible");
      await navigator.clipboard.writeText(code);
      adminLogCredentialAccess(credId, { targetType: "TOTP", action: "COPY" }).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button className="cred-action-btn" onClick={handleCopy}
      title="Copier le code 2FA"
      style={{ color: copied ? "#10B981" : "#3D6B6B" }}>
      <i className={`bi ${loading ? "bi-arrow-repeat spin" : copied ? "bi-check2" : "bi-shield-lock"}`}/>
    </button>
  );
}

// ─────────────────────────────────────────────
//  MENU "..." — ACTIONS PAR LIGNE
// ─────────────────────────────────────────────

function CredRowMenu({ cred, onEdit, onDelete, onShare }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fermer en cliquant en dehors
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={ref} style={{ position:"relative" }} onClick={e => e.stopPropagation()}>
      <button
        className="cred-action-btn"
        onClick={() => setOpen(v => !v)}
        title="Actions"
        style={{ fontSize:16 }}
      >
        <i className="bi bi-list"/>
      </button>

      {open && (
        <div style={{
          position:"absolute", right:0, top:"calc(100% + 4px)", zIndex:200,
          background:"var(--card-bg)", border:"1px solid var(--border)", borderRadius:10,
          boxShadow:"0 8px 24px rgba(0,0,0,.12)", minWidth:200, overflow:"hidden"
        }}>
          {/* Copier identifiant */}
          {(cred.username || cred.host) && (
            <MenuCopyItem
              label="Copier l'identifiant"
              icon="bi-person"
              value={cred.username || cred.host}
              onDone={() => setOpen(false)}
            />
          )}

          {/* Copier mot de passe */}
          <MenuCopyPasswordItem credId={cred.id} onDone={() => setOpen(false)}/>

          {/* Copier code 2FA */}
          {cred.hasTotpSecret && (
            <MenuCopyTotpItem credId={cred.id} onDone={() => setOpen(false)}/>
          )}

          {/* Copier URL */}
          {cred.url && (
            <MenuCopyItem
              label="Copier l'URL"
              icon="bi-link-45deg"
              value={cred.url}
              onDone={() => setOpen(false)}
            />
          )}

          {/* Ouvrir URL */}
          {cred.url && (
            <a
              href={/^https?:\/\//i.test(cred.url) ? cred.url : `https://${cred.url}`}
              target="_blank" rel="noopener noreferrer"
              style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", fontSize:13, color:"var(--text-1)", textDecoration:"none", transition:"background .12s" }}
              onMouseEnter={e => e.currentTarget.style.background="var(--navy-hover)"}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}
              onClick={() => setOpen(false)}
            >
              <i className="bi bi-box-arrow-up-right" style={{ color:"var(--text-2)", width:16 }}/>
              Ouvrir le site
            </a>
          )}

          {/* Séparateur */}
          <div style={{ borderTop:"1px solid var(--border)", margin:"4px 0" }}/>

          {/* Partager (lien sécurisé à usage unique) */}
          <button
            onClick={() => { setOpen(false); onShare(); }}
            style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", fontSize:13, color:"var(--text-1)", background:"none", border:"none", cursor:"pointer", width:"100%", transition:"background .12s" }}
            onMouseEnter={e => e.currentTarget.style.background="var(--navy-hover)"}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}
          >
            <i className="bi bi-share" style={{ color:"var(--text-2)", width:16 }}/>
            Partager
          </button>

          {/* Séparateur */}
          <div style={{ borderTop:"1px solid var(--border)", margin:"4px 0" }}/>

          {/* Modifier */}
          <button
            onClick={() => { setOpen(false); onEdit(); }}
            style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", fontSize:13, color:"var(--text-1)", background:"none", border:"none", cursor:"pointer", width:"100%", transition:"background .12s" }}
            onMouseEnter={e => e.currentTarget.style.background="var(--navy-hover)"}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}
          >
            <i className="bi bi-pencil" style={{ color:"var(--text-2)", width:16 }}/>
            Modifier
          </button>

          {/* Supprimer */}
          <button
            onClick={() => { setOpen(false); onDelete(); }}
            style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", fontSize:13, color:"#DC2626", background:"none", border:"none", cursor:"pointer", width:"100%", transition:"background .12s" }}
            onMouseEnter={e => e.currentTarget.style.background="var(--danger-dim)"}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}
          >
            <i className="bi bi-trash" style={{ width:16 }}/>
            Supprimer
          </button>
        </div>
      )}
    </div>
  );
}

function MenuCopyItem({ label, icon, value, onDone }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => { setCopied(false); onDone(); }, 1200);
  };
  return (
    <button onClick={handleCopy}
      style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", fontSize:13, color: copied ? "#10B981" : "var(--text-1)", background:"none", border:"none", cursor:"pointer", width:"100%", transition:"background .12s" }}
      onMouseEnter={e => e.currentTarget.style.background="var(--navy-hover)"}
      onMouseLeave={e => e.currentTarget.style.background="transparent"}
    >
      <i className={`bi ${copied ? "bi-check2" : icon}`} style={{ color: copied ? "#10B981" : "var(--text-2)", width:16 }}/>
      {copied ? "Copié !" : label}
    </button>
  );
}

function MenuCopyPasswordItem({ credId, onDone }) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleCopy = async () => {
    setLoading(true);
    try {
      const res = await getPassword(credId);
      await navigator.clipboard.writeText(res.data);
      adminLogCredentialAccess(credId, { targetType: "PASSWORD", action: "COPY" }).catch(() => {});
      setCopied(true);
      setTimeout(() => { setCopied(false); onDone(); }, 1200);
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={handleCopy}
      style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", fontSize:13, color: copied ? "#10B981" : "var(--text-1)", background:"none", border:"none", cursor:"pointer", width:"100%", transition:"background .12s" }}
      onMouseEnter={e => e.currentTarget.style.background="var(--navy-hover)"}
      onMouseLeave={e => e.currentTarget.style.background="transparent"}
    >
      <i className={`bi ${loading ? "bi-arrow-repeat spin" : copied ? "bi-check2" : "bi-key"}`} style={{ color: copied ? "#10B981" : "var(--text-2)", width:16 }}/>
      {copied ? "Copié !" : "Copier le mot de passe"}
    </button>
  );
}

function MenuCopyTotpItem({ credId, onDone }) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleCopy = async () => {
    setLoading(true);
    try {
      const res = await adminGetTotpCode(credId);
      const code = res?.data?.code || res?.code;
      if (!code) throw new Error("Code 2FA indisponible");
      await navigator.clipboard.writeText(code);
      adminLogCredentialAccess(credId, { targetType: "TOTP", action: "COPY" }).catch(() => {});
      setCopied(true);
      setTimeout(() => { setCopied(false); onDone(); }, 1200);
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={handleCopy}
      style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", fontSize:13, color: copied ? "#10B981" : "var(--text-1)", background:"none", border:"none", cursor:"pointer", width:"100%", transition:"background .12s" }}
      onMouseEnter={e => e.currentTarget.style.background="var(--navy-hover)"}
      onMouseLeave={e => e.currentTarget.style.background="transparent"}
    >
      <i className={`bi ${loading ? "bi-arrow-repeat spin" : copied ? "bi-check2" : "bi-shield-lock"}`} style={{ color: copied ? "#10B981" : "#3D6B6B", width:16 }}/>
      {copied ? "Copié !" : "Copier le code 2FA"}
    </button>
  );
}

// ─────────────────────────────────────────────
//  CRÉATION / ÉDITION CREDENTIAL
//
//  Déléguée au composant partagé CredentialModal (components/ui/CredentialModal.jsx) —
//  mêmes champs, même générateur de mot de passe et même 2FA que dans les autres
//  pages (MyPasswordsPage, PasswordsPage client). Voir son rendu plus bas, dans le
//  bloc "Modales" du composant PasswordsPage.
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
//  COMPOSANT — MODALE GESTION DES DOSSIERS
// ─────────────────────────────────────────────

function FolderModal({ companyId, folders, onSave, onClose }) {
  const [name,    setName]    = useState("");
  const [color,   setColor]   = useState(FOLDER_COLORS[0]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleCreate = async () => {
    if (!name.trim()) return setError("Le nom est obligatoire.");
    setLoading(true);
    try {
      await createFolder({ name, color, company: { id: companyId } });
      setName("");
      setError("");
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce dossier ? Les credentials reviennent à la racine.")) return;
    try {
      await deleteFolder(id);
      onSave();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 440 }}>

        <div className="modal-header">
          <h3>Gérer les dossiers</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg"/></button>
        </div>

        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}

          {/* Créer un dossier */}
          <div className="form-group">
            <label className="itm-label">Nouveau dossier</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="itm-input" style={{ flex: 1 }} value={name}
                onChange={e => setName(e.target.value)} placeholder="Nom du dossier"/>
              <button className="btn-primary" onClick={handleCreate} disabled={loading}>
                <i className="bi bi-plus-lg"/>
              </button>
            </div>

            {/* Sélecteur de couleur */}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {FOLDER_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  style={{
                    width: 24, height: 24, borderRadius: 6, background: c, border: "none",
                    cursor: "pointer", outline: color === c ? "2px solid white" : "none",
                    outlineOffset: 2,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Liste des dossiers existants */}
          <div style={{ marginTop: 16 }}>
            <label className="itm-label">Dossiers existants</label>
            {folders.length === 0 && (
              <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 8 }}>Aucun dossier pour cette entreprise.</p>
            )}
            {folders.map(f => (
              <div key={f.id} className="folder-row">
                <div className="folder-dot" style={{ background: f.color || "#6741D9" }}/>
                <span style={{ flex: 1, fontSize: 13, color: "var(--text-1)" }}>{f.name}</span>
                <button className="icon-btn-danger" onClick={() => handleDelete(f.id)}>
                  <i className="bi bi-trash"/>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT — MODALE POLITIQUE DE MOT DE PASSE
// ─────────────────────────────────────────────

function PolicyModal({ companyId, onClose }) {
  const [policy,  setPolicy]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  // Charge la politique au montage
  useEffect(() => {
    getPolicy(companyId)
      .then(res => setPolicy(res.data || defaultPolicy()))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [companyId]);

  const defaultPolicy = () => ({
    minLength: 8, requireUppercase: true, requireDigit: true,
    requireSpecial: false, minUppercase: 1, minDigits: 1, minSpecials: 0,
  });

  const set = (key, value) => setPolicy(p => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await savePolicy(companyId, policy);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 460 }}>

        <div className="modal-header">
          <h3>Politique de mot de passe</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg"/></button>
        </div>

        <div className="modal-body">
          {loading && <p style={{ color: "var(--text-3)", fontSize: 13 }}>Chargement…</p>}
          {error   && <div className="form-error">{error}</div>}

          {!loading && policy && (
            <>
              {/* Longueur minimale */}
              <div className="form-group">
                <label className="itm-label">Longueur minimale : <strong>{policy.minLength}</strong></label>
                <input type="range" min="6" max="32" value={policy.minLength}
                  onChange={e => set("minLength", parseInt(e.target.value))}
                  style={{ width: "100%" }}/>
              </div>

              {/* Cases à cocher */}
              {[
                { key: "requireUppercase", label: "Majuscule(s) requise(s)",         countKey: "minUppercase" },
                { key: "requireDigit",     label: "Chiffre(s) requis",               countKey: "minDigits"    },
                { key: "requireSpecial",   label: "Caractère(s) spécial(aux) requis", countKey: "minSpecials" },
              ].map(({ key, label, countKey }) => (
                <div key={key} className="policy-row">
                  <label className="policy-check-label">
                    <input
                      type="checkbox"
                      checked={policy[key]}
                      onChange={e => set(key, e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    {label}
                  </label>
                  {/* Nombre minimum — visible uniquement si la règle est activée */}
                  {policy[key] && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, marginLeft: 24 }}>
                      <label style={{ fontSize: 12, color: "var(--text-3)" }}>Minimum :</label>
                      <input
                        type="number" min="1" max="10"
                        value={policy[countKey]}
                        onChange={e => set(countKey, parseInt(e.target.value))}
                        className="itm-input" style={{ width: 60 }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving
              ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</>
              : saved
                ? <><i className="bi bi-check2 me-1"/>Enregistré !</>
                : "Enregistrer"
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  LIGNE D'UNE CREDENTIAL — liste groupée par lettre
//  Même structure/style que CredRow dans MyPasswordsPage.jsx, pour que
//  l'affichage des credentials soit identique partout dans l'application.
// ─────────────────────────────────────────────

/** Miniature de ligne — vrai logo (favicon) si une marque est détectée
 *  depuis le nom/l'URL, sinon l'illustration THUMB générique par type. */
function RowThumb({ cred, meta, ThumbFn }) {
  const type = (cred.type || "OTHER").toUpperCase();
  const [failedDomain, setFailedDomain] = useState(null);
  const eligible = BRAND_AWARE_TYPES.has(type);
  const domain = eligible ? resolveBrandDomain(cred.name, cred.url, cred.host) : null;

  if (domain && domain !== failedDomain) {
    return (
      <div style={{ width: 56, height: 34, background: "#fff", display: "flex",
                    alignItems: "center", justifyContent: "center" }}>
        <img
          key={domain}
          src={faviconSrc(domain, 128)}
          alt=""
          onError={() => setFailedDomain(domain)}
          style={{ width: 30, height: 30, objectFit: "contain" }}
        />
      </div>
    );
  }

  return ThumbFn ? ThumbFn(56, 34) : (
    <div style={{ width: 56, height: 34, borderRadius: 6, background: meta.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <i className={`bi ${meta.icon}`} style={{ color: "#fff", fontSize: 15 }}/>
    </div>
  );
}

function CredRow({ cred, onView, onEdit, onDelete, onShare }) {
  const type    = (cred.type || "OTHER").toUpperCase();
  const meta    = typeMeta(type);
  const ThumbFn = THUMB[type];

  return (
    // Clic sur la ligne → ouvre la modale de visualisation
    <div className="cred-row" onClick={() => onView(cred)}>
      {/* Vignette */}
      <div className="cred-thumb">
        <RowThumb cred={cred} meta={meta} ThumbFn={ThumbFn}/>
      </div>

      {/* Nom + identifiant */}
      <div className="cred-info">
        <span className="cred-name">{cred.name}</span>
        {cred.username && <span className="cred-login">{cred.username}</span>}
        {!cred.username && cred.host && <span className="cred-login">{cred.host}{cred.port ? `:${cred.port}` : ""}</span>}
      </div>

      {/* Menu burger — juste après le nom */}
      <div className="cred-actions" onClick={e => e.stopPropagation()}>
        <CredRowMenu cred={cred} onEdit={onEdit} onDelete={onDelete} onShare={() => onShare(cred)}/>
      </div>

      {/* Badges */}
      <div className="cred-badges">
        {cred.folder && (
          <span className="cred-folder-badge">
            <i className="bi bi-folder me-1"/>{cred.folder.name}
          </span>
        )}
        <span className="cred-type-badge" style={{ background: meta.color + "22", color: meta.color, borderColor: meta.color + "44" }}>
          {meta.label}
        </span>
        {cred.hasTotpSecret && (
          <span style={{ fontSize:11, fontWeight:600, color:"#10B981", background:"rgba(16,185,129,.1)", padding:"2px 7px", borderRadius:20 }}>
            <i className="bi bi-shield-check me-1"/>2FA
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT — MODALE DE VISUALISATION
// ─────────────────────────────────────────────

function CredViewModal({ cred, onClose, onEdit, onDelete, onUpdate, onShare }) {
  const meta = typeMeta(cred.type);
  const [password,   setPassword]   = useState(null);
  const [showPwd,    setShowPwd]    = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Révélation individuelle des champs personnalisés de type "Mot de passe"
  const [revealedCF, setRevealedCF] = useState({});

  const type = (cred.type || "OTHER").toUpperCase();
  const brandDomain = BRAND_AWARE_TYPES.has(type) && !logoFailed
    ? resolveBrandDomain(cred.name, cred.url, cred.host)
    : null;

  // Charge le mot de passe déchiffré depuis le backend
  const loadPassword = async () => {
    if (password !== null) { setShowPwd(v => !v); return; }
    setPwdLoading(true);
    try {
      const res = await getPassword(cred.id);
      setPassword(res.data);
      setShowPwd(true);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setPwdLoading(false);
    }
  };

  const copyPwd = async () => {
    let pwd = password;
    if (!pwd) { const res = await getPassword(cred.id); pwd = res.data; setPassword(pwd); }
    await navigator.clipboard.writeText(pwd);
    adminLogCredentialAccess(cred.id, { targetType: "PASSWORD", action: "COPY" }).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 460 }}>

        {/* Bannière colorée — vrai logo si la marque est détectée */}
        <div className="vw-banner" style={{ background: `linear-gradient(135deg, ${meta.color}cc, ${meta.color}66)` }}>
          {brandDomain ? (
            <div style={{ width: 54, height: 54, borderRadius: 14, background: "#fff",
                          boxShadow: "0 4px 14px rgba(0,0,0,.2)", display: "flex",
                          alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <img
                key={brandDomain}
                src={faviconSrc(brandDomain, 128)}
                alt=""
                onError={() => setLogoFailed(true)}
                style={{ width: 30, height: 30, objectFit: "contain" }}
              />
            </div>
          ) : (
            <i className={`bi ${meta.icon}`} style={{ fontSize: 38, color: "rgba(255,255,255,.9)" }}/>
          )}
          <div className="vw-banner-text">
            <span className="vw-banner-name">{cred.name}</span>
            <span className="vw-banner-type">{meta.label} · {cred.scope === "COMPANY" ? "Entreprise" : "Personnel"}</span>
          </div>
          <button className="modal-close" style={{ position: "absolute", top: 10, right: 12 }} onClick={onClose}>
            <i className="bi bi-x-lg"/>
          </button>
        </div>

        <div className="modal-body">

          {/* Actions */}
          <div className="vw-action-row">
            <button className="vw-btn-edit" onClick={() => { onClose(); onEdit(cred); }}>
              <i className="bi bi-pencil me-1"/>Modifier
            </button>
            <button className="vw-btn-edit" onClick={() => { onClose(); onShare(cred); }}>
              <i className="bi bi-share me-1"/>Partager
            </button>
            {!delConfirm ? (
              <button className="vw-btn-delete" onClick={() => setDelConfirm(true)}>
                <i className="bi bi-trash me-1"/>Supprimer
              </button>
            ) : (
              <div className="vw-delete-confirm">
                <span>Confirmer ?</span>
                <button className="vw-confirm-yes" onClick={() => { onClose(); onDelete(cred.id); }}>Oui</button>
                <button className="vw-confirm-no"  onClick={() => setDelConfirm(false)}>Non</button>
              </div>
            )}
            {cred.folder && (
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>
                <i className="bi bi-folder me-1"/>{cred.folder.name}
              </span>
            )}
          </div>

          <div className="vw-fields">
            {/* Username */}
            {cred.username && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-person me-1"/>Nom d'utilisateur</span>
                <div className="vw-field-value-row">
                  <span className="vw-field-value">{cred.username}</span>
                  <button className="vw-icon-btn" onClick={() => navigator.clipboard.writeText(cred.username)}>
                    <i className="bi bi-copy"/>
                  </button>
                </div>
              </div>
            )}

            {/* Mot de passe */}
            <div className="vw-field">
              <span className="vw-field-label"><i className="bi bi-key me-1"/>Mot de passe</span>
              <div className="vw-field-value-row">
                <span className="vw-field-value" style={{ fontFamily: showPwd ? "monospace" : "inherit" }}>
                  {showPwd && password ? password : "••••••••"}
                </span>
                <button className="vw-icon-btn" onClick={loadPassword} disabled={pwdLoading}>
                  {pwdLoading
                    ? <i className="bi bi-arrow-repeat vw-spin"/>
                    : <i className={`bi ${showPwd ? "bi-eye-slash" : "bi-eye"}`}/>
                  }
                </button>
                <button className="vw-icon-btn" onClick={copyPwd}>
                  <i className={`bi ${copied ? "bi-check2" : "bi-copy"}`}
                     style={{ color: copied ? "#69DB7C" : undefined }}/>
                </button>
              </div>
            </div>

            {/* URL */}
            {cred.url && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-link-45deg me-1"/>URL</span>
                <a href={/^https?:\/\//i.test(cred.url || "") ? cred.url : `https://${cred.url}`} target="_blank" rel="noopener noreferrer" className="vw-field-link" onClick={e => e.stopPropagation()}>{cred.url} <i className="bi bi-box-arrow-up-right" style={{fontSize:10}}/></a>
              </div>
            )}

            {/* Host */}
            {cred.host && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-hdd-network me-1"/>Host / Port</span>
                <span className="vw-field-value">{cred.host}{cred.port ? `:${cred.port}` : ""}</span>
              </div>
            )}

            {/* Notes */}
            {cred.notes && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-sticky me-1"/>Notes</span>
                <span className="vw-field-value vw-notes">{cred.notes}</span>
              </div>
            )}

            {/* Champs personnalisés — masqués avec œil si de type "Mot de passe" */}
            {(() => {
              let customFields = [];
              try { customFields = cred.customFields ? JSON.parse(cred.customFields) : []; } catch {}
              return customFields.map((cf, i) => {
                const isPwd    = cf.type === "PASSWORD";
                const revealed = !!revealedCF[i];
                return (
                  <div className="vw-field" key={i}>
                    <span className="vw-field-label"><i className="bi bi-input-cursor-text me-1"/>{cf.name}</span>
                    <div className="vw-field-value-row">
                      <span className="vw-field-value" style={{ fontFamily: isPwd && revealed ? "monospace" : "inherit" }}>
                        {isPwd && !revealed ? "••••••••" : cf.value}
                      </span>
                      {isPwd && (
                        <button className="vw-icon-btn" onClick={() => {
                          if (!revealed) adminLogCredentialAccess(cred.id, { targetType: "CUSTOM_FIELD", action: "VIEW", fieldName: cf.name }).catch(() => {});
                          setRevealedCF(p => ({ ...p, [i]: !p[i] }));
                        }} title={revealed ? "Masquer" : "Révéler"}>
                          <i className={`bi ${revealed ? "bi-eye-slash" : "bi-eye"}`}/>
                        </button>
                      )}
                      <button className="vw-icon-btn" onClick={() => {
                        if (isPwd) adminLogCredentialAccess(cred.id, { targetType: "CUSTOM_FIELD", action: "COPY", fieldName: cf.name }).catch(() => {});
                        navigator.clipboard.writeText(cf.value);
                      }}>
                        <i className="bi bi-copy"/>
                      </button>
                    </div>
                  </div>
                );
              });
            })()}

            {/* ── 2FA / TOTP ── l'admin peut configurer le 2FA sur n'importe quelle credential */}
            <TotpWidget
              credentialId={cred.id}
              hasTotpSecret={!!cred.hasTotpSecret}
              onUpdate={onUpdate}
              admin
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT — MODALE DE PARTAGE SÉCURISÉ
//
//  Lien public à usage unique : le destinataire n'a pas besoin de compte
//  Deskora. Sécurité assurée uniquement par l'entropie du token (voir
//  PublicCredentialShareController côté backend) — pas de code additionnel.
// ─────────────────────────────────────────────

const SHARE_STATUS_META = {
  ACTIVE:  { label: "Actif",    color: "#2F9E44", bg: "rgba(47,158,68,.1)"  },
  VIEWED:  { label: "Consulté", color: "#6B7280", bg: "rgba(107,114,128,.1)" },
  EXPIRED: { label: "Expiré",   color: "#F08C00", bg: "rgba(240,140,0,.1)"  },
  REVOKED: { label: "Révoqué",  color: "#DC2626", bg: "rgba(220,38,38,.1)"  },
};

function ShareLinkModal({ cred, onClose }) {
  const [durationHours, setDurationHours] = useState(24);
  const [includeNotes,  setIncludeNotes]  = useState(false);
  const [creating,      setCreating]      = useState(false);
  const [createError,   setCreateError]   = useState("");
  const [newLink,        setNewLink]      = useState(null); // { token, shareUrl, expiresAt }
  const [copied,         setCopied]       = useState(false);

  const [shares,        setShares]        = useState([]);
  const [sharesLoading, setSharesLoading] = useState(true);
  const [revoking,      setRevoking]      = useState(null); // token en cours de révocation

  const loadShares = async () => {
    setSharesLoading(true);
    try {
      const res = await adminGetShares(cred.id);
      setShares(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err) {
      // Non bloquant — l'historique est un plus, pas indispensable à la création.
    } finally {
      setSharesLoading(false);
    }
  };

  useEffect(() => { loadShares(); /* eslint-disable-next-line */ }, [cred.id]);

  const handleCreate = async () => {
    setCreating(true);
    setCreateError("");
    try {
      const link = await adminCreateShare(cred.id, { durationHours, includeNotes });
      setNewLink(link);
      loadShares();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    if (!newLink?.shareUrl) return;
    await navigator.clipboard.writeText(newLink.shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async (token) => {
    setRevoking(token);
    try {
      await adminRevokeShare(token);
      loadShares();
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 480 }}>
        <div className="vw-banner" style={{ background: "linear-gradient(135deg, #6741D9cc, #6741D966)" }}>
          <i className="bi bi-share" style={{ fontSize: 34, color: "rgba(255,255,255,.9)" }}/>
          <div className="vw-banner-text">
            <span className="vw-banner-name">Partager</span>
            <span className="vw-banner-type">{cred.name}</span>
          </div>
          <button className="modal-close" style={{ position: "absolute", top: 10, right: 12 }} onClick={onClose}>
            <i className="bi bi-x-lg"/>
          </button>
        </div>

        <div className="modal-body">
          {!newLink ? (
            <>
              <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
                Génère un lien à usage unique, sans compte requis pour le
                consultant. Le lien devient définitivement inutilisable dès
                qu'il a été ouvert une fois, ou à l'expiration choisie —
                selon la première des deux échéances.
              </p>

              <div style={{ marginBottom: 14 }}>
                <span className="vw-field-label" style={{ display: "block", marginBottom: 8 }}>
                  <i className="bi bi-clock-history me-1"/>Durée de validité
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  {[1, 24, 72].map((h) => (
                    <button
                      key={h}
                      onClick={() => setDurationHours(h)}
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: durationHours === h ? "2px solid #6741D9" : "1px solid var(--border)",
                        background: durationHours === h ? "rgba(103,65,217,.08)" : "var(--card-bg)",
                        color: durationHours === h ? "#6741D9" : "var(--text-1)",
                        cursor: "pointer", transition: "all .12s",
                      }}
                    >
                      {h === 1 ? "1 heure" : h === 24 ? "24 heures" : "72 heures"}
                    </button>
                  ))}
                </div>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-1)", cursor: "pointer", marginBottom: 16 }}>
                <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} />
                Inclure les notes de la credential dans le lien
              </label>

              {createError && (
                <div style={{ fontSize: 12.5, color: "#DC2626", background: "var(--danger-dim)", border: "1px solid #FFD7D7", borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>
                  {createError}
                </div>
              )}

              <button
                onClick={handleCreate}
                disabled={creating}
                className="vw-btn-edit"
                style={{ width: "100%", justifyContent: "center", opacity: creating ? .6 : 1 }}
              >
                {creating ? <i className="bi bi-arrow-repeat vw-spin me-1"/> : <i className="bi bi-link-45deg me-1"/>}
                Créer le lien
              </button>
            </>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#2F9E44", marginBottom: 8 }}>
                <i className="bi bi-check-circle me-1"/>Lien créé — usage unique
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", background: "var(--navy-mid)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px" }}>
                <span style={{ fontSize: 12.5, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {newLink.shareUrl}
                </span>
                <button className="vw-icon-btn" onClick={copyLink} title="Copier">
                  <i className={`bi ${copied ? "bi-check2" : "bi-copy"}`} style={{ color: copied ? "#69DB7C" : undefined }}/>
                </button>
              </div>
              <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 8 }}>
                Ce lien ne sera plus jamais affiché — copiez-le et envoyez-le
                au destinataire dès maintenant.
              </p>
              <button
                onClick={() => { setNewLink(null); setCreateError(""); }}
                style={{ fontSize: 12.5, color: "#6741D9", background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 4 }}
              >
                Créer un autre lien
              </button>
            </div>
          )}

          {/* Historique des partages */}
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 14 }}>
            <span className="vw-field-label" style={{ display: "block", marginBottom: 8 }}>
              <i className="bi bi-list-ul me-1"/>Historique des partages
            </span>

            {sharesLoading ? (
              <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>Chargement…</div>
            ) : shares.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>Aucun lien créé pour cette credential.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                {shares.map((s) => {
                  const meta = SHARE_STATUS_META[s.status] || SHARE_STATUS_META.EXPIRED;
                  return (
                    <div key={s.token} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "6px 8px", borderRadius: 8, background: "var(--navy-mid)" }}>
                      <span style={{ fontWeight: 600, color: meta.color, background: meta.bg, borderRadius: 20, padding: "2px 8px", fontSize: 11 }}>
                        {meta.label}
                      </span>
                      <span style={{ color: "var(--text-2)", flex: 1 }}>
                        Créé le {s.createdAt ? new Date(s.createdAt).toLocaleString("fr-FR") : "—"}
                        {s.createdBy ? ` par ${s.createdBy}` : ""}
                      </span>
                      {s.status === "ACTIVE" && (
                        <button
                          onClick={() => handleRevoke(s.token)}
                          disabled={revoking === s.token}
                          style={{ fontSize: 11, color: "#DC2626", background: "none", border: "1px solid #FFD7D7", borderRadius: 6, padding: "3px 8px", cursor: "pointer" }}
                        >
                          {revoking === s.token ? "…" : "Révoquer"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

/**
 * Page de gestion des mots de passe.
 *
 * L'admin sélectionne d'abord une entreprise via CompanySelector.
 * Ensuite la liste des credentials, dossiers et politique se chargent.
 */
export default function PasswordsPage() {

  const { user } = useAuth();

  // Entreprise sélectionnée — initialisée avec l'entreprise de l'utilisateur connecté
  const [company, setCompany] = useState(() => {
    if (user?.company) return user.company;
    return null;
  });

  // L'initialisation ci-dessus ne s'exécute qu'au tout premier rendu : si
  // `user` n'est pas encore chargé à ce moment-là (ex: arrivée directe sur
  // /passwords via un rafraîchissement de page), `company` reste bloqué à
  // null même une fois que user.company devient disponible. On resynchronise
  // ici dès que c'est le cas, tant que l'admin n'a rien sélectionné lui-même.
  useEffect(() => {
    if (!company && user?.company) {
      setCompany(user.company);
    }
  }, [user, company]);

  const companyId   = company?.id   || null;
  const companyName = company?.name || null;

  // ── Données ──────────────────────────────────
  const [credentials, setCredentials] = useState([]);
  const [folders,     setFolders]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  // ── Filtres ───────────────────────────────────
  const [search,       setSearch]       = useState("");
  const [filterScope,  setFilterScope]  = useState("ALL");
  const [filterType,   setFilterType]   = useState("ALL");
  const [filterFolder, setFilterFolder] = useState("ALL");

  // ── Modales ───────────────────────────────────
  const [showForm,      setShowForm]      = useState(false);
  const [editData,      setEditData]      = useState(null);   // null = création
  const [viewCred,      setViewCred]      = useState(null);
  const [shareCred,     setShareCred]     = useState(null);   // credential en cours de partage
  const [showFolders,   setShowFolders]   = useState(false);
  const [showPolicy,    setShowPolicy]    = useState(false);

  // Charge les données dès que l'entreprise change
  useEffect(() => {
    if (companyId) loadAll();
  }, [companyId]);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [credsRes, foldersRes] = await Promise.all([
        getCredentials(companyId),
        getFolders(companyId),
      ]);
      // Filtrage défensif : cette vue admin ne doit JAMAIS afficher ni
      // permettre de révéler des credentials PERSONAL (appartenant à un
      // utilisateur précis), même si le backend en renvoyait un jour par
      // erreur. Seuls les credentials COMPANY sont légitimes ici.
      const companyOnly = (credsRes.data || []).filter(c => c.scope === "COMPANY");
      setCredentials(companyOnly);
      setFolders(foldersRes.data    || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Après sauvegarde d'une credential
  const handleSaved = () => {
    setShowForm(false);
    setEditData(null);
    loadAll();
  };

  // Suppression d'une credential
  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette credential ?")) return;
    try {
      await adminDeleteCredential(id);
      setCredentials(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Filtrage côté client (instantané, pas de nouvel appel API)
  const filtered = useMemo(() => {
    return credentials
      .filter(c => filterScope  === "ALL" || c.scope  === filterScope)
      .filter(c => filterType   === "ALL" || c.type   === filterType)
      .filter(c => filterFolder === "ALL"
        ? true
        : filterFolder === "NONE"
          ? !c.folder
          : c.folder?.id === filterFolder)
      .filter(c =>
        (c.name     || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.username || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.host     || "").toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [credentials, filterScope, filterType, filterFolder, search]);

  // Groupement alphabétique — même présentation que MyPasswordsPage.jsx
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(c => {
      const letter = (c.name?.[0] || "#").toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(c);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // ── Cas : aucune entreprise sélectionnée ──────
  if (!companyId) {
    return (
      <>
        <Topbar title="Mots de passe" subtitle="Administration"/>
        <div className="page-body">
          <div className="cs-page-selector">
            <i className="bi bi-shield-lock" style={{ fontSize: 40, opacity: .3, marginBottom: 16 }}/>
            <p style={{ fontSize: 14, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>
              Sélectionnez une entreprise
            </p>
            <p style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 20 }}>
              Choisissez une entreprise pour gérer ses mots de passe.
            </p>
            <CompanySelector
              selectedId={companyId}
              onChange={setCompany}
            />
          </div>
        </div>
        <style>{CSS}</style>
      </>
    );
  }

  return (
    <>
      <Topbar title="Mots de passe" subtitle={companyName || "Administration"}/>
      <div className="page-body">

        {/* ── En-tête : sélecteur d'entreprise + actions principales ── */}
        <div className="pwd-header anim-fade-up">
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, flexWrap: "wrap" }}>

            {/* Sélecteur d'entreprise — permet de changer sans recharger la page */}
            <CompanySelector
              selectedId={companyId}
              onChange={setCompany}
            />

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Bouton Ajouter */}
              <button className="pwd-add-btn" onClick={() => { setEditData(null); setShowForm(true); }}>
                <i className="bi bi-plus-lg"/><span>Ajouter</span>
              </button>

              {/* Dossiers */}
              <button className="pwd-action-btn" onClick={() => setShowFolders(true)}>
                <i className="bi bi-folder"/><span>Dossiers</span>
              </button>

              {/* Politique */}
              <button className="pwd-action-btn" onClick={() => setShowPolicy(true)}>
                <i className="bi bi-shield-check"/><span>Politique</span>
              </button>
            </div>
          </div>

          {/* Compteur */}
          <span className="pwd-count">
            {filtered.length} / {credentials.length} credential{credentials.length > 1 ? "s" : ""}
          </span>
        </div>

        {/* ── Filtres ── */}
        <div className="pwd-toolbar anim-fade-up">

          {/* Recherche */}
          <div className="pwd-search-wrap">
            <i className="bi bi-search pwd-search-icon"/>
            <input className="pwd-search" placeholder="Rechercher…"
              value={search} onChange={e => setSearch(e.target.value)}/>
            {search && <button className="pwd-search-clear" onClick={() => setSearch("")}><i className="bi bi-x"/></button>}
          </div>

          {/* Scope */}
          <select className="itm-input" style={{ width: "auto" }}
            value={filterScope} onChange={e => setFilterScope(e.target.value)}>
            <option value="ALL">Tous les scopes</option>
            <option value="COMPANY">Entreprise</option>
          </select>

          {/* Type */}
          <select className="itm-input" style={{ width: "auto" }}
            value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="ALL">Tous les types</option>
            {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>

          {/* Dossier */}
          {folders.length > 0 && (
            <select className="itm-input" style={{ width: "auto" }}
              value={filterFolder} onChange={e => setFilterFolder(e.target.value)}>
              <option value="ALL">Tous les dossiers</option>
              <option value="NONE">Sans dossier</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
        </div>

        {/* ── Chargement ── */}
        {loading && (
          <div className="pwd-empty">
            <i className="bi bi-arrow-repeat spin" style={{ fontSize: 32, opacity: .4 }}/>
            <p>Chargement…</p>
          </div>
        )}

        {/* ── Erreur ── */}
        {!loading && error && (
          <div className="pwd-empty">
            <i className="bi bi-exclamation-triangle" style={{ color: "#DC2626", opacity: 1 }}/>
            <p style={{ color: "#DC2626" }}>{error}</p>
            <button className="pwd-add-btn" onClick={loadAll} style={{ marginTop: 10 }}>Réessayer</button>
          </div>
        )}

        {/* ── Liste des credentials — groupée par lettre, même présentation partout ── */}
        {!loading && !error && (
          <div className="itm-card anim-fade-up" style={{ padding: 0 }}>

            {/* État vide */}
            {filtered.length === 0 && (
              <div className="pwd-empty">
                <i className="bi bi-shield-lock"/>
                <p>Aucune credential trouvée</p>
                <small>Modifiez les filtres ou ajoutez une nouvelle credential.</small>
              </div>
            )}

            {grouped.map(([letter, items]) => (
              <div key={letter}>
                <div className="pwd-letter-sep">{letter}</div>
                {items.map((cred, idx) => (
                  <div key={cred.id} style={{ borderBottom: idx < items.length - 1 ? "1px solid var(--border)" : "none" }}>
                    <CredRow
                      cred={cred}
                      onView={setViewCred}
                      onEdit={() => { setEditData(cred); setShowForm(true); }}
                      onDelete={() => handleDelete(cred.id)}
                      onShare={setShareCred}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modales ── */}
      {showForm && (
        <CredentialModal
          editData={editData}
          defaultScope="COMPANY"
          companyId={companyId}
          folders={folders}
          onExternalSave={handleSaved}
          onExternalClose={() => { setShowForm(false); setEditData(null); }}
        />
      )}

      {viewCred && (
        <CredViewModal
          cred={viewCred}
          onClose={() => setViewCred(null)}
          onEdit={(c) => { setViewCred(null); setEditData(c); setShowForm(true); }}
          onDelete={(id) => { setViewCred(null); handleDelete(id); }}
          onUpdate={() => { setViewCred(null); loadAll(); }}
          onShare={(c) => setShareCred(c)}
        />
      )}

      {shareCred && (
        <ShareLinkModal
          cred={shareCred}
          onClose={() => setShareCred(null)}
        />
      )}

      {showFolders && (
        <FolderModal
          companyId={companyId}
          folders={folders}
          onSave={loadAll}
          onClose={() => setShowFolders(false)}
        />
      )}

      {showPolicy && (
        <PolicyModal
          companyId={companyId}
          onClose={() => setShowPolicy(false)}
        />
      )}

      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────

const CSS = `
/* Sélecteur centré (état vide) */
.cs-page-selector{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;text-align:center}

/* En-tête */
.pwd-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:14px;flex-wrap:wrap}
.pwd-add-btn{display:flex;align-items:center;gap:7px;background:#E63329;border:none;border-radius:10px;color:#fff;font-size:13px;font-weight:600;padding:9px 16px;cursor:pointer;transition:filter .18s}
.pwd-add-btn:hover{filter:brightness(1.12)}
.pwd-action-btn{display:flex;align-items:center;gap:6px;background:var(--card-bg);border:1px solid var(--border-md);border-radius:10px;color:var(--text-2);font-size:13px;padding:8px 14px;cursor:pointer;transition:all .15s}
.pwd-action-btn:hover{background:var(--navy-mid);color:var(--text-1)}
.pwd-count{font-size:11px;color:var(--text-3)}

/* Toolbar */
.pwd-toolbar{display:flex;align-items:center;gap:10px;margin-bottom:14px;flex-wrap:wrap}
.pwd-search-wrap{position:relative;display:flex;align-items:center}
.pwd-search-icon{position:absolute;left:10px;color:var(--text-3);font-size:13px;pointer-events:none}
.pwd-search{background:var(--navy-mid);border:1px solid var(--border);border-radius:8px;color:var(--text-1);font-size:13px;padding:7px 32px;width:220px;outline:none;transition:border-color .15s}
.pwd-search:focus{border-color:#E63329}
.pwd-search::placeholder{color:var(--text-3)}
.pwd-search-clear{position:absolute;right:8px;background:none;border:none;color:var(--text-3);cursor:pointer;font-size:14px;padding:0}

/* Liste groupée par lettre — même présentation partout dans l'application */
.pwd-letter-sep{padding:6px 18px 5px;font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--text-3);background:var(--navy-mid);border-bottom:1px solid var(--border)}
.cred-row{display:flex;align-items:center;gap:14px;padding:10px 18px;transition:background .15s;cursor:pointer}
.cred-row:hover{background:var(--navy-mid)}
.cred-thumb{width:56px;height:34px;border-radius:6px;overflow:hidden;flex-shrink:0;line-height:0}
.cred-thumb svg{display:block;width:100%;height:100%}
.cred-info{flex:0 1 260px;min-width:0;display:flex;flex-direction:column}
.cred-name{font-size:14px;font-weight:600;color:var(--text-1);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cred-login{font-size:11.5px;color:var(--text-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px}
.cred-badges{display:flex;align-items:center;gap:6px;flex-shrink:0;margin-left:auto}
.cred-folder-badge{font-size:10px;font-weight:500;padding:2px 8px;border-radius:20px;color:var(--text-2);background:var(--navy-mid);border:1px solid var(--border);white-space:nowrap}
.cred-type-badge{font-size:10px;font-weight:500;padding:2px 8px;border-radius:20px;border:1px solid;white-space:nowrap}
.scope-badge{font-size:10px;font-weight:600;padding:2px 8px;border-radius:20px;white-space:nowrap}
.scope-company{background:rgba(230,51,41,.1);color:#E63329;border:1px solid rgba(230,51,41,.25)}
.scope-personal{background:rgba(47,158,68,.1);color:#2F9E44;border:1px solid rgba(47,158,68,.25)}
.cred-actions{display:flex;gap:4px;flex-shrink:0}
.cred-action-btn{width:28px;height:28px;border-radius:7px;background:none;border:1px solid transparent;color:var(--text-3);font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
.cred-action-btn:hover{background:var(--navy-soft);color:var(--text-1)}
.cred-action-btn.danger:hover{background:rgba(220,38,38,.1);color:#DC2626}

/* Vide */
.pwd-empty{display:flex;flex-direction:column;align-items:center;padding:60px 20px;color:var(--text-3);text-align:center}
.pwd-empty i{font-size:42px;margin-bottom:14px;opacity:.4}
.pwd-empty p{font-size:14px;font-weight:500;color:var(--text-2);margin:0 0 4px}
.pwd-empty small{font-size:12px}

/* Modales */
.modal-overlay{position:fixed;inset:0;z-index:1055;background:rgba(26,26,26,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px;animation:vwFadeIn .15s ease}
@keyframes vwFadeIn{from{opacity:0}to{opacity:1}}
.modal-box{background:var(--card-bg);border:1px solid var(--border);border-radius:18px;box-shadow:0 40px 120px rgba(0,0,0,.25);width:100%;max-width:560px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;animation:vwSlideIn .2s cubic-bezier(.34,1.56,.64,1)}
@keyframes vwSlideIn{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px 14px;border-bottom:1px solid var(--border)}
.modal-header h3{font-size:15px;font-weight:700;color:var(--text-1);margin:0}
.modal-close{background:var(--navy-mid);border:none;cursor:pointer;color:var(--text-2);width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:12px}
.modal-close:hover{background:#E63329;color:#fff}
.modal-body{padding:18px 20px;overflow-y:auto;flex:1;display:flex;flex-direction:column;gap:0}
.modal-footer{padding:14px 20px;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:10px}

/* Renforcement mobile — la modale doit toujours tenir dans l'écran, même
   quand la barre d'adresse mobile change de hauteur (100dvh plutôt que
   90vh, plus fiable que vh sur Chrome/Safari mobile). */
@media (max-width:480px){
  .modal-overlay{padding:10px}
  .modal-box{max-height:min(90vh,calc(100dvh - 20px))}
}

/* Formulaire */
.form-group{margin-bottom:14px}
.form-error{background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.25);border-radius:8px;color:#DC2626;font-size:12px;padding:8px 12px;margin-bottom:14px}
.btn-primary{background:#E63329;border:none;border-radius:10px;color:#fff;font-size:13px;font-weight:600;padding:8px 18px;cursor:pointer;display:flex;align-items:center;gap:6px;transition:filter .15s}
.btn-primary:hover{filter:brightness(1.1)}
.btn-primary:disabled{opacity:.5;cursor:not-allowed}
.btn-secondary{background:var(--card-bg);border:1px solid var(--border-md);border-radius:10px;color:var(--text-2);font-size:13px;padding:8px 18px;cursor:pointer;transition:all .15s}
.btn-secondary:hover{background:var(--navy-mid);color:var(--text-1)}

/* Générateur de mot de passe */
.pwd-gen{background:var(--navy-mid);border:1px solid var(--border);border-radius:10px;padding:10px 12px;margin-top:8px}
.pwd-gen-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.pwd-gen-label{font-size:11px;color:var(--text-3);white-space:nowrap}
.pwd-gen-slider{flex:1;min-width:80px;accent-color:#E63329}
.pwd-gen-btn{display:flex;align-items:center;gap:5px;background:var(--card-bg);border:1px solid var(--border-md);border-radius:8px;color:var(--text-2);font-size:12px;padding:5px 10px;cursor:pointer;white-space:nowrap;transition:all .15s}
.pwd-gen-btn:hover{background:#E63329;border-color:#E63329;color:#fff}
.pwd-gen-btn:disabled{opacity:.4;cursor:not-allowed}
.pwd-gen-preview{display:flex;align-items:center;gap:8px;margin-top:8px;padding-top:8px;border-top:1px solid var(--border)}

/* Dossiers */
.folder-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid rgba(0,0,0,.05)}
.folder-dot{width:12px;height:12px;border-radius:3px;flex-shrink:0}
.icon-btn-danger{width:26px;height:26px;border-radius:6px;background:none;border:none;cursor:pointer;color:#DC2626;display:flex;align-items:center;justify-content:center;font-size:13px;transition:background .15s}
.icon-btn-danger:hover{background:rgba(220,38,38,.1)}

/* Politique */
.policy-row{padding:10px 0;border-bottom:1px solid rgba(0,0,0,.05)}
.policy-check-label{display:flex;align-items:center;font-size:13px;color:var(--text-1);cursor:pointer}

/* Modale visualisation */
.vw-banner{width:100%;height:96px;flex-shrink:0;display:flex;align-items:center;justify-content:center;gap:14px;padding:0 24px;position:relative}
.vw-banner-text{display:flex;flex-direction:column}
.vw-banner-name{font-size:1.05rem;font-weight:700;color:#fff}
.vw-banner-type{font-size:11px;color:rgba(255,255,255,.85);margin-top:2px}
.vw-action-row{display:flex;align-items:center;gap:8px;margin-bottom:14px}
.vw-btn-edit{display:flex;align-items:center;gap:5px;background:var(--card-bg);border:1px solid var(--border-md);border-radius:8px;color:var(--text-2);font-size:12px;padding:6px 12px;cursor:pointer;transition:all .15s}
.vw-btn-edit:hover{background:var(--navy-mid);color:var(--text-1)}
.vw-btn-delete{display:flex;align-items:center;gap:5px;background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.2);border-radius:8px;color:#DC2626;font-size:12px;padding:6px 12px;cursor:pointer}
.vw-delete-confirm{display:flex;align-items:center;gap:8px;font-size:12px;color:#DC2626}
.vw-confirm-yes{background:#DC2626;border:none;border-radius:6px;color:#fff;font-size:11px;font-weight:600;padding:3px 10px;cursor:pointer}
.vw-confirm-no{background:var(--navy-mid);border:none;border-radius:6px;color:var(--text-2);font-size:11px;padding:3px 10px;cursor:pointer}
.vw-fields{display:flex;flex-direction:column;gap:10px}
.vw-field{display:flex;flex-direction:column;gap:4px;background:var(--navy-mid);border:1px solid var(--border);border-radius:10px;padding:10px 14px}
.vw-field-label{font-size:10px;font-weight:600;color:var(--text-3);text-transform:uppercase;letter-spacing:.08em}
.vw-field-value-row{display:flex;align-items:center;gap:8px}
.vw-field-value{flex:1;font-size:13px;color:var(--text-1);word-break:break-all}
.vw-field-link{font-size:13px;color:#E63329;text-decoration:none}
.vw-notes{white-space:pre-wrap;color:var(--text-2)}
.vw-icon-btn{width:28px;height:28px;border-radius:7px;background:var(--card-bg);border:1px solid var(--border-md);color:var(--text-3);font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0}
.vw-icon-btn:hover{background:#E63329;border-color:#E63329;color:#fff}
.vw-icon-btn:disabled{opacity:.4;cursor:not-allowed}

/* Champs personnalisés — façon PasswordBoss (sélecteur de type) */
.cf-row{border:1px solid var(--border);border-radius:10px;padding:10px;margin-bottom:8px;background:var(--navy-mid)}
.cf-row-top{display:grid;grid-template-columns:1fr 118px auto;gap:8px;margin-bottom:8px;align-items:center}
.cf-type-select{cursor:pointer;-webkit-appearance:menulist;appearance:menulist}
.cf-del-btn{background:none;border:1px solid var(--border-md);border-radius:8px;width:38px;height:38px;cursor:pointer;color:var(--text-3);flex-shrink:0;transition:all .15s}
.cf-del-btn:hover{color:#E63329;border-color:#E63329}
.cf-value-wrap{position:relative}
.cf-value-icons{position:absolute;right:5px;top:50%;transform:translateY(-50%);display:flex;gap:1px}
.cf-value-icons button{background:none;border:none;cursor:pointer;color:var(--text-3);padding:6px;font-size:13px;display:flex;align-items:center;justify-content:center;transition:color .15s}
.cf-value-icons button:hover{color:var(--text-1)}
.cf-add-btn{display:flex;align-items:center;gap:6px;background:none;border:1px dashed rgba(0,0,0,.15);border-radius:8px;padding:8px 12px;cursor:pointer;color:var(--text-2);font-size:13px;width:100%;justify-content:center}
.cf-checkbox-wrap{display:flex;align-items:center;gap:10px;cursor:pointer;padding:2px 0}
.cf-checkbox-wrap input{position:absolute;opacity:0;width:0;height:0}
.cf-checkbox-slider{position:relative;flex-shrink:0;width:38px;height:22px;border-radius:99px;background:rgba(0,0,0,.15);transition:background .18s}
.cf-checkbox-slider::before{content:"";position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:var(--card-bg);box-shadow:0 1px 3px rgba(0,0,0,.25);transition:transform .18s}
.cf-checkbox-wrap input:checked + .cf-checkbox-slider{background:#E63329}
.cf-checkbox-wrap input:checked + .cf-checkbox-slider::before{transform:translateX(16px)}
.cf-checkbox-state{font-size:.82rem;color:var(--text-2)}

/* Animations */
@keyframes spin{to{transform:rotate(360deg)}}
.spin{animation:spin .7s linear infinite;display:inline-block}
@keyframes vwSpin{to{transform:rotate(360deg)}}
.vw-spin{animation:vwSpin .7s linear infinite;display:inline-block}
`;